import * as React from 'react';
import styled from 'styled-components';
import { DSButton } from '../../Generic';
import { DSTypography } from '../../mui';

const StyledPostWrapper = styled.div<{ $bottomSpacing?: number }>`
    position: relative;
    display: block;

    ${props => props.$bottomSpacing && `margin-bottom: ${props.theme.spacing(props.$bottomSpacing)}px; `}

    border: 1px solid #dadce0;
    color: rgba(0, 0, 0, 0.54);

    border-radius: 5px;

    @media print {
        page-break-inside: avoid;
    }
`;

interface StyledPostImageWrapperProps {
    $imgPaddingBottom: React.CSSProperties['paddingBottom'];
}

const StyledPostImageWrapper = styled.div<StyledPostImageWrapperProps>`
    position: relative;

    width: 100%;

    border-top-left-radius: 5px;
    border-top-right-radius: 5px;

    overflow: hidden;

    img {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;

        object-fit: contain;
    }

    &:after {
        content: '';
        display: block;
        padding-bottom: ${props => props.$imgPaddingBottom};
    }
`;

const StyledPostDescriptionWrapper = styled.div`
    padding: 15px;
`;

const StyledPostDescription = styled(DSTypography)<{ $fixedCaptionHeight: boolean }>`
    ${props => (props.$fixedCaptionHeight ? 'height: 85px;' : 'max-height: 85px;')}

    display: -webkit-box;

    overflow: hidden;

    text-overflow: ellipsis;
    white-space: pre-wrap;

    -webkit-line-clamp: 5;
    -webkit-box-orient: vertical;
`;

const StyledHoverWrapper = styled.div`
    position: absolute;
    top: 0;
    left: 0;

    display: flex;
    width: 100%;
    height: 100%;
    align-items: center;
    justify-content: center;

    opacity: 0;

    background-color: rgba(0, 0, 0, 0.5);

    border-radius: 5px;

    transition: opacity 0.5s;

    &:hover {
        opacity: 1;
    }
`;

export type ReportPostProps = {
    caption?: string;
    children?: React.ReactNode;
    fixedCaptionHeight?: boolean;
    imgRatioPadding?: React.CSSProperties['paddingBottom'];
    postBottomSpacing?: number;
    thumbnailTitle?: string;
    thumbnailUrl?: string;
} & (
    | {
          permalinkText: string;
          permalinkUrl?: string;
          permalinkTitle?: string;
      }
    | {
          permalinkText: undefined;
          permalinkUrl: undefined;
          permalinkTitle: undefined;
      }
);

export const ReportPost: React.FunctionComponent<ReportPostProps> = ({
    caption,
    children,
    fixedCaptionHeight = false,
    imgRatioPadding = '100%',
    permalinkText = 'View',
    permalinkTitle = 'Permalink',
    permalinkUrl,
    postBottomSpacing,
    thumbnailTitle = 'Post thumbnail',
    thumbnailUrl,
}) => (
    <StyledPostWrapper $bottomSpacing={postBottomSpacing}>
        {thumbnailUrl && (
            <StyledPostImageWrapper $imgPaddingBottom={imgRatioPadding}>
                <img src={thumbnailUrl} title={thumbnailTitle} />
            </StyledPostImageWrapper>
        )}
        {(caption || fixedCaptionHeight) && (
            <StyledPostDescriptionWrapper>
                <StyledPostDescription $fixedCaptionHeight={fixedCaptionHeight} variant="body2">
                    {caption}
                </StyledPostDescription>
            </StyledPostDescriptionWrapper>
        )}
        {children}
        {permalinkUrl && permalinkText && (
            <StyledHoverWrapper>
                <a href={permalinkUrl} rel="noreferrer" target="_blank" title={permalinkTitle}>
                    <DSButton color="primary" size="small">
                        {permalinkText}
                    </DSButton>
                </a>
            </StyledHoverWrapper>
        )}
    </StyledPostWrapper>
);
ReportPost.displayName = 'ReportPost';
